import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import useFetchObjects from "../../api/useFetchObjects";
import { formatJalaliDate } from "../utils/dateFormat";

const AccountView = ({ open, onClose, data, token }) => {
  const formatAmount = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value ?? "-";
    return n.toFixed(2);
  };

  const formatRate = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return value ?? "-";
    return n.toFixed(6);
  };

  const { data: transactionsData, isLoading: txLoading } = useFetchObjects(
    ["account-transactions-view", data?.id],
    data?.id ? `shop/account-transactions/?page_size=all&account=${data.id}` : "",
    token
  );

  const transactions = transactionsData?.results || transactionsData || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          pb: 1.5,
          pt: 2.5,
          px: 3.5,
          fontWeight: 800,
          fontSize: 28,
          color: "#0f4c81",
          borderBottom: "1px solid #e5e7eb",
          background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
        }}
      >
        Account Information
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, px: 0.5 }}>
          <TableContainer
            component={Paper}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(15,76,129,0.08)",
              overflow: "hidden",
              mt: 1.5,
            }}
          >
            <Box
              sx={{
                p: 2.25,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.3fr 0.8fr 1fr 0.8fr 1fr" },
                columnGap: 2,
                rowGap: 1,
              }}
            >
              {["Account Name", "Currency", "Opening Balance", "Branch", "Notes"].map((label) => (
                <Typography key={label} sx={{ fontWeight: 800, color: "#0f4c81" }}>
                  {label}
                </Typography>
              ))}
              {[
                data?.name || "-",
                data?.currency_title || data?.currency?.title || "-",
                data?.balance ?? data?.opening_balance ?? "-",
                data?.shop_name || data?.shop?.name || "-",
                data?.note || "-",
              ].map((value, idx) => (
                <Typography key={idx} sx={{ fontWeight: 600 }}>
                  {value}
                </Typography>
              ))}
            </Box>
          </TableContainer>

          <Paper
            sx={{
              mt: 3.5,
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              boxShadow: "0 8px 24px rgba(15,76,129,0.08)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 2.25, bgcolor: "#f8fbff", borderBottom: "1px solid #e5e7eb" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, fontSize: "2rem", color: "#0f4c81" }}>
                Account Transactions
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Currency</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Exchange Rate</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Converted Amount</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {txLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No transactions found for this account.
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((tx) => (
                      <TableRow key={tx.id} hover>
                        <TableCell>{formatJalaliDate(tx.transaction_date)}</TableCell>
                        <TableCell>{tx.transaction_type || "-"}</TableCell>
                        <TableCell>{formatAmount(tx.amount)}</TableCell>
                        <TableCell>{tx.transaction_currency_title || "-"}</TableCell>
                        <TableCell>{formatRate(tx.exchange_rate)}</TableCell>
                        <TableCell>{formatAmount(tx.converted_amount)}</TableCell>
                        <TableCell>{tx.description || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ mr: 2, mb: 1, fontWeight: 700 }}>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountView;
